import { DynamicFormFieldModel, DynamicFormModel } from "@electricMotorShop/Types/DynamicFormModels";
import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { Draft } from "immer";

type DynamicFormState = Readonly<{
    forms: DynamicFormModel[];
}>;

const initialState: DynamicFormState = { forms: [] };

const DynamicFormData = createTypedReducerWithImmer(initialState, {
    "Custom/DynamicForm/AddFormToState": (
        draft: Draft<DynamicFormState>,
        action: { formName: string },
    ) => {
        const formIndex = draft.forms.findIndex(f => f.name === action.formName);
        if (formIndex < 0) {
            const initialFormState: DynamicFormModel = {
                name: action.formName,
                isSubmitting: false,
                displayValidationErrors: false,
                fields: [],
            };
            draft.forms.push(initialFormState);
        }
    },
    "Custom/DynamicForm/SetDynamicFormFieldValue": (
        draft: Draft<DynamicFormState>,
        action: { formName: string, formField: DynamicFormFieldModel },
    ) => {
        const formIndex = draft.forms.findIndex(f => f.name === action.formName);
        if (formIndex < 0) {
            return;
        }
        const fieldIndex = draft.forms[formIndex].fields.findIndex(f => f.key === action.formField.key);
        if (fieldIndex < 0) {
            draft.forms[formIndex].fields.push(action.formField);
        } else {
            draft.forms[formIndex].fields[fieldIndex].value = action.formField.value;
            draft.forms[formIndex].fields[fieldIndex].hasError = action.formField.hasError;
            draft.forms[formIndex].fields[fieldIndex].error = action.formField.error;
            if (action.formField.type === "DynamicFormFileUpload") {
                draft.forms[formIndex].fields[fieldIndex].label = action.formField.label;
            }
        }
    },
    "Custom/DynamicForm/RemoveDynamicFormFieldValue": (
        draft: Draft<DynamicFormState>,
        action: { formName: string, formFieldKey: string },
    ) => {
        const formIndex = draft.forms.findIndex(f => f.name === action.formName);
        if (formIndex < 0) {
            return;
        }
        const fieldIndex = draft.forms[formIndex].fields.findIndex(f => f.key === action.formFieldKey);
        if (fieldIndex > -1) {
            draft.forms[formIndex].fields.splice(fieldIndex, 1);
        }
    },
    "Custom/DynamicForm/BeginSubmitForm": (
        draft: Draft<DynamicFormState>,
        action: { formName: string },
    ) => {
        const formIndex = draft.forms.findIndex(f => f.name === action.formName);
        if (formIndex < 0) {
            return;
        }
        draft.forms[formIndex].displayValidationErrors = true;
        draft.forms[formIndex].isSubmitting = true;
    },
    "Custom/DynamicForm/EndSubmitForm": (
        draft: Draft<DynamicFormState>,
        action: { formName: string },
    ) => {
        const formIndex = draft.forms.findIndex(f => f.name === action.formName);
        if (formIndex < 0) {
            return;
        }
        draft.forms[formIndex].displayValidationErrors = true;
        draft.forms[formIndex].isSubmitting = false;
    },
    "Custom/DynamicForm/ClearForm": (
        draft: Draft<DynamicFormState>,
        action: { formName: string },
    ) => {
        const formIndex = draft.forms.findIndex(f => f.name === action.formName);
        if (formIndex < 0) {
            return;
        }
        draft.forms[formIndex].displayValidationErrors = false;
        draft.forms[formIndex].isSubmitting = false;        
        draft.forms[formIndex].fields = [];
    }
});

export default (reducers as any).xcentiumDynamicForms = DynamicFormData;

export type CustomDynamicFormState = {
    xcentiumDynamicForms: DynamicFormState;
} & ApplicationState;

type Reducers = {
    dynamicFormReducer: typeof DynamicFormData;
};

export type XcentiumDynamicFormActions = Parameters<Reducers[keyof Reducers]>[1] | AnyAction;
